

























import { Component, Vue, Prop } from 'vue-property-decorator';
import { SmartQuery } from 'vue-apollo-decorator';
import { GQL_invoice_invoice } from '@/queries/__generated__/invoice';
import { GQL_customer_customer } from '@/queries/__generated__/customer';
import {
  GQL_invoiceEfakturaStatus,
  GQL_invoiceEfakturaStatus_invoice,
} from '@/queries/__generated__/invoiceEfakturaStatus';
import gql from 'graphql-tag';

const INVOICE_SEND_BY_EFAKTURA = gql`
  mutation sendInvoiceByEfaktura($invoiceId: ID!) {
    sendInvoiceByEfaktura(input: { invoiceId: $invoiceId }) {
      efakturaSubmission {
        id
        humanStatus
        sendingStatus
        pending
        shipmentId
        errorMessage
        sentAt
      }
    }
  }
`;

// The poll against Mastercard runs server side (EfakturaStatusPollJob); this
// only re-reads what that job has already written, so it can be cheap and frequent.
const INVOICE_EFAKTURA_STATUS = gql`
  query invoiceEfakturaStatus($id: ID!) {
    invoice(id: $id) {
      id
      latestEfakturaSubmission {
        id
        humanStatus
        sendingStatus
        pending
        shipmentId
        errorMessage
        sentAt
      }
    }
  }
`;

const STATUS_POLL_INTERVAL = 60 * 1000;

@Component
export default class InvoiceSendByEfakturaButton extends Vue {
  @Prop() private invoice!: GQL_invoice_invoice;
  @Prop() private customer!: GQL_customer_customer;

  // Mirrors the submission into the invoice the rest of the page is rendering from, so
  // the status the poll picks up shows everywhere, not just on this button.
  @SmartQuery<InvoiceSendByEfakturaButton, GQL_invoiceEfakturaStatus>({
    query: () => INVOICE_EFAKTURA_STATUS,
    variables() {
      return { id: this.invoice.id };
    },
    pollInterval: STATUS_POLL_INTERVAL,
    skip() {
      return !this.isPending;
    },
    update: data => data.invoice,
    result(this: InvoiceSendByEfakturaButton, { data }) {
      if (data?.invoice) this.$set(this.invoice, 'latestEfakturaSubmission', data.invoice.latestEfakturaSubmission);
    },
  }) private polledInvoice!: GQL_invoiceEfakturaStatus_invoice;

  private performingRequest = false;

  private get submission() {
    return this.invoice.latestEfakturaSubmission;
  }

  private get isEligible(): boolean {
    return !!this.customer?.efakturaEligible;
  }

  // Waiting on Mastercard: the shipment is in flight and nothing can be done about it
  // from here until the poll settles it.
  private get isPending(): boolean {
    return !!this.submission?.pending;
  }

  private get isProcessed(): boolean {
    return this.submission?.humanStatus === 'PROCESSED';
  }

  private get isFailed(): boolean {
    return this.submission?.humanStatus === 'REJECTED' || this.submission?.humanStatus === 'FAILED';
  }

  private get isBusy(): boolean {
    return this.performingRequest || this.isPending;
  }

  // A failure is the one state that hands the button back: everything else is either in
  // flight, already delivered, or not something this customer can receive.
  private get isDisabled(): boolean {
    return !this.isEligible || this.isBusy || this.isProcessed;
  }

  private get disabledReason(): string {
    if (!this.isEligible) return this.$t('Invoice.Efaktura.privatePersonOnly') as string;
    return '';
  }

  private get statusText(): string {
    if (this.isFailed) {
      return this.submission?.errorMessage || (this.$t('Invoice.Efaktura.failed') as string);
    }
    if (this.isProcessed) return this.$t('Invoice.Efaktura.succeeded') as string;
    if (this.isPending) return this.$t('Invoice.Efaktura.pending') as string;
    return '';
  }

  private get statusIcon(): string {
    if (this.isFailed) return 'exclamation-circle';
    if (this.isProcessed) return 'check-circle';
    return '';
  }

  private get statusClass(): string {
    if (this.isFailed) return 'status-error';
    if (this.isProcessed) return 'status-success';
    return '';
  }

  public send() {
    if (this.isDisabled) return;

    this.performingRequest = true;
    this.$sMessage.info(this.$t('Invoice.Efaktura.sending'));

    this.$apollo.mutate({
      mutation: INVOICE_SEND_BY_EFAKTURA,
      variables: { invoiceId: this.invoice.id },
    }).then(({ data }) => {
      const submission = data.sendInvoiceByEfaktura.efakturaSubmission;
      this.$set(this.invoice, 'latestEfakturaSubmission', submission);

      if (submission?.pending) {
        this.$sMessage.success(this.$t('Invoice.Efaktura.sent'));
      } else {
        this.$sMessage.error(submission?.errorMessage || this.$t('Invoice.Efaktura.failed'));
      }
    }).catch(() => {
      // A GraphQL error (no agreement, blocked receiver, MPS unreachable) is surfaced by
      // the global handler with the server's own message.
    }).finally(() => {
      this.performingRequest = false;
    });
  }
}
